"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { Referral } from "@/src/models/documents/referral";

interface UVVCReferralInstructionsProps {
  referral: Referral;
  salesforceData?: {
    type?: string;
    Name?: string;
    Patient_Name__c?: string;
    Id?: string;
    Subject?: string;
    HealthCloudGA__Priority__c?: string;
    Owner_Full_Name__c?: string;
    Referral_Source_NPI__c?: string;
    Primary_Care_Physician_NPI__c?: string;
    Market__c?: string;
  } | null;
  error?: string;
}

const PATIENT_TYPE_LEAD = "Lead";

export function UVVCReferralInstructions({ referral, salesforceData, error }: UVVCReferralInstructionsProps) {
  const salesforcePatient = salesforceData || {};

  const patientName =
    salesforcePatient.type === PATIENT_TYPE_LEAD ? salesforcePatient.Name : salesforcePatient.Patient_Name__c;

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      {error && <ErrorAlert compact={true} message={error} />}

      {!error && salesforceData && (
        <EhrInstructionsCard>
          <div className="text-sm text-muted-foreground">
            <div className="space-y-6 text-foreground">
              <div className="mb-4">
                <h3 className="text-md font-semibold mb-4 text-center">Information in Salesforce</h3>
                <p>
                  The following information is available in Salesforce. Verify that the information is correct, and then
                  mark the sync complete.
                </p>
                <br />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Patient Name: </span> <CopyableContent text={patientName || "N/A"} />
                  </div>
                  <div>
                    <span className="font-medium">ID: </span>
                    <CopyableContent text={salesforcePatient.Id || "N/A"} />
                  </div>
                  <div>
                    <span className="font-medium">Type: </span>
                    <CopyableContent text={salesforcePatient.type || "N/A"} />
                  </div>
                  <div>
                    <span className="font-medium">Subject: </span>
                    <CopyableContent text={salesforcePatient.Subject || "N/A"} />
                  </div>
                  {salesforcePatient.type === PATIENT_TYPE_LEAD && (
                    <>
                      <div>
                        <span className="font-medium">Priority: </span>{" "}
                        {salesforcePatient.HealthCloudGA__Priority__c || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Owner (Assigned To): </span>
                        <CopyableContent text={salesforcePatient.Owner_Full_Name__c || "N/A"} />
                      </div>
                      <div>
                        <span className="font-medium">Referral Source NPI: </span>
                        <CopyableContent text={salesforcePatient.Referral_Source_NPI__c || "N/A"} />
                      </div>
                      <div>
                        <span className="font-medium">Primary Care Physician NPI: </span>
                        <CopyableContent text={salesforcePatient.Primary_Care_Physician_NPI__c || "N/A"} />
                      </div>
                      <div>
                        <span className="font-medium">Market: </span>{" "}
                        <CopyableContent text={salesforcePatient.Market__c || "N/A"} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </EhrInstructionsCard>
      )}

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </div>
  );
}
